'use client';

import React, { useState, forwardRef } from 'react';
import styles from './GlobalDropdown.module.css';

export interface GlobalDropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface GlobalDropdownProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: GlobalDropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  error?: string;
  showErrorText?: boolean;
}

export const GlobalDropdown = forwardRef<HTMLSelectElement, GlobalDropdownProps>(
  ({
    label,
    value,
    onChange,
    options,
    placeholder = 'Select',
    disabled = false,
    required = false,
    className = '',
    error,
    showErrorText = true,
  }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const labelText = label ?? placeholder;
    const hasValue = value !== '';

    const handleFocus = () => {
      if (!disabled) setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (!disabled) onChange(e.target.value);
    };

    return (
      <div className={`${styles.inputContainer} ${disabled ? styles.disabled : ''} ${className}`}>
        <div className={`${styles.inputWrapper} ${disabled ? styles.wrapperDisabled : ''}`}>
          <select
            ref={ref}
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            required={required}
            className={`${styles.select} ${error ? styles.error : ''} ${isFocused ? styles.focused : ''} ${hasValue ? styles.hasValue : ''} ${disabled ? styles.selectDisabled : ''}`}
          >
            <option value="" disabled hidden />
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>
          <label className={styles.label}>{labelText}</label>
          <svg
            className={styles.chevron}
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 9l6 6 6-6"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        {error && showErrorText ? (
          <span className={styles.errorMessage}>{error}</span>
        ) : null}
      </div>
    );
  }
);

GlobalDropdown.displayName = 'GlobalDropdown';
